import type { AnalysisResult, ValidationIssue } from "../domain/types";
import { APP_VERSION, createReportMetadata, projectChecksum } from "../report/metadata";

interface SensitivityRow {
  factor: number;
  result: Partial<AnalysisResult> & { fs?: number };
}

interface LegacyApi {
  getState(): Record<string, any>;
  replaceState(state: Record<string, any>): void;
  getResult(): AnalysisResult | null;
  evaluateSensitivity(parameter: string, factors: number[]): SensitivityRow[];
}

declare global {
  interface Window {
    NendatYeuLegacy?: LegacyApi;
  }
}

const STEPS = [
  { title: "Công trình", cards: ["emb-card"] },
  { title: "Địa tầng", cards: ["layers-card", "sketch-card"] },
  { title: "Phương án", cards: ["treatment-card"] },
  { title: "Điều kiện biên", cards: ["general-card"] },
  { title: "Kết quả", cards: ["metrics-card", "charts-card", "detailed-table-card", "cdm-verification-card"] },
] as const;
const ALL_CARDS = STEPS.flatMap((step) => [...step.cards]);
const AUTOSAVE_KEY = "nendatyeu.autosave.v3";
const HISTORY_KEY = "nendatyeu.history.v3";
const COMPARISON_KEY = "nendatyeu.comparisons.v3";

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

function fieldIdForPath(path: string): string | undefined {
  const direct: Record<string, string> = {
    "foundation.mFactor": "emb-m-factor", "foundation.drainage": "drainage-boundary",
    "foundation.shearMode": "shear-analysis-mode", "pvd.spacing": "pvd-spacing",
    "pvd.depth": "pvd-depth", "cdm.depth": "cdm-depth",
  };
  if (direct[path]) return direct[path];
  const match = path.match(/^soilLayers\.(\d+)\.(.+)$/);
  if (!match) return undefined;
  const row = document.querySelectorAll<HTMLTableRowElement>("#soil-table-body tr")[Number(match[1])];
  const field = match[2] ?? "";
  const selectors: Record<string, string> = {
    thickness: 'input[title*="dày"]', gamma: 'input[title*="tự nhiên"]', Cv: 'input[title*="Cv"]',
  };
  const input = row?.querySelector<HTMLElement>(selectors[field] ?? "input");
  if (input && !input.id) input.id = `soil-${match[1]}-${field}`;
  return input?.id;
}

function sourceForInput(input: HTMLInputElement): string {
  const card = input.closest<HTMLElement>(".card")?.id;
  if (card === "treatment-card") return "TCCS/TCVN theo phương án";
  if (card === "layers-card") return "Hồ sơ khảo sát địa chất";
  if (card === "general-card") return "Điều kiện biên thiết kế";
  return "Số liệu công trình";
}

export function initStage3Ux(): void {
  const boot = () => {
    const api = window.NendatYeuLegacy;
    const main = document.querySelector<HTMLElement>("main");
    if (!api || !main || document.getElementById("stage3-toolbar")) return;

    let activeStep = Number(localStorage.getItem("nendatyeu.wizardStep") ?? 0);
    let showAll = false;
    let dirty = false;
    let undoStack: Record<string, any>[] = [];
    let redoStack: Record<string, any>[] = [];
    let focusSnapshot: Record<string, any> | null = null;
    let autosaveTimer: ReturnType<typeof setTimeout> | undefined;

    const toolbar = element("section", "stage3-toolbar");
    toolbar.id = "stage3-toolbar";
    toolbar.innerHTML = `
      <div class="wizard-steps" role="tablist" aria-label="Các bước lập bài toán">
        ${STEPS.map((step, index) => `<button type="button" class="wizard-step" data-step="${index}" role="tab"><span>${index + 1}</span>${step.title}</button>`).join("")}
      </div>
      <div class="project-controls">
        <button type="button" class="btn btn-secondary btn-sm" id="wizard-prev">← Trước</button>
        <button type="button" class="btn btn-secondary btn-sm" id="wizard-next">Tiếp →</button>
        <button type="button" class="btn btn-secondary btn-sm" id="wizard-all">Xem tất cả</button>
        <span class="save-status" id="save-status" aria-live="polite">Chưa thay đổi</span>
        <button type="button" class="btn btn-secondary btn-sm" id="undo-project" disabled>Hoàn tác</button>
        <button type="button" class="btn btn-secondary btn-sm" id="redo-project" disabled>Làm lại</button>
        <button type="button" class="btn btn-secondary btn-sm" id="open-history">Lịch sử</button>
        <button type="button" class="btn btn-secondary btn-sm" id="open-comparison">So sánh</button>
        <button type="button" class="btn btn-secondary btn-sm" id="open-sensitivity">Độ nhạy</button>
      </div>`;
    main.before(toolbar);

    const errors = element("section", "engineering-errors");
    errors.id = "engineering-errors";
    errors.setAttribute("aria-live", "polite");
    toolbar.after(errors);

    const drawer = element("aside", "engineering-drawer");
    drawer.id = "engineering-drawer";
    drawer.hidden = true;
    document.body.appendChild(drawer);
    const stickyPreview = element("section", "engineering-sketch-preview");
    stickyPreview.id = "sticky-sketch-preview";
    stickyPreview.hidden = true;
    stickyPreview.innerHTML = '<div class="sticky-sketch-header"><strong>Sơ đồ 2D cập nhật trực tiếp</strong><button type="button" aria-label="Thu gọn sơ đồ">−</button></div><canvas width="800" height="260"></canvas>';
    document.body.appendChild(stickyPreview);
    const stickyCanvas = stickyPreview.querySelector("canvas") as HTMLCanvasElement;
    const sourceCanvas = document.getElementById("soil-canvas") as HTMLCanvasElement | null;
    const sourceCard = document.getElementById("sketch-card");
    let previewUpdateTimer: ReturnType<typeof setTimeout> | undefined;

    const syncStickyPreview = () => {
      if (!sourceCanvas) return;
      const context = stickyCanvas.getContext("2d");
      if (!context) return;
      context.clearRect(0, 0, stickyCanvas.width, stickyCanvas.height);
      context.drawImage(sourceCanvas, 0, 0, stickyCanvas.width, stickyCanvas.height);
    };
    const updateStickyPreview = () => {
      if (!sourceCard || !sourceCanvas) return;
      const toolbarBottom = toolbar.getBoundingClientRect().bottom;
      const sourceRect = sourceCard.getBoundingClientRect();
      const mainRect = main.getBoundingClientRect();
      const shouldShow = showAll && window.innerWidth >= 1024 && sourceRect.bottom < toolbarBottom + 12 && mainRect.bottom > 180;
      stickyPreview.hidden = !shouldShow;
      if (shouldShow) syncStickyPreview();
    };
    const scheduleStickySync = () => {
      if (previewUpdateTimer) clearTimeout(previewUpdateTimer);
      previewUpdateTimer = setTimeout(() => {
        syncStickyPreview();
        updateStickyPreview();
      }, 400);
    };
    stickyPreview.querySelector("button")?.addEventListener("click", () => {
      stickyPreview.classList.toggle("collapsed");
      const collapsed = stickyPreview.classList.contains("collapsed");
      const button = stickyPreview.querySelector("button");
      if (button) button.textContent = collapsed ? "+" : "−";
    });
    window.addEventListener("scroll", updateStickyPreview, { passive: true });
    window.addEventListener("resize", updateStickyPreview);
    document.addEventListener("input", scheduleStickySync, true);
    document.addEventListener("change", scheduleStickySync, true);

    const renderWizard = () => {
      activeStep = Math.max(0, Math.min(STEPS.length - 1, activeStep));
      const currentStep = STEPS[activeStep] ?? STEPS[0];
      main.classList.toggle("wizard-mode", !showAll);
      ALL_CARDS.forEach((id) => {
        const card = document.getElementById(id);
        if (card) card.hidden = !showAll && !currentStep.cards.includes(id as never);
      });
      toolbar.querySelectorAll<HTMLButtonElement>(".wizard-step").forEach((button, index) => {
        button.classList.toggle("active", index === activeStep && !showAll);
        button.setAttribute("aria-selected", String(index === activeStep && !showAll));
      });
      const prev = document.getElementById("wizard-prev") as HTMLButtonElement;
      const next = document.getElementById("wizard-next") as HTMLButtonElement;
      prev.disabled = showAll || activeStep === 0;
      next.disabled = showAll || activeStep === STEPS.length - 1;
      document.getElementById("wizard-all")!.textContent = showAll ? "Theo từng bước" : "Xem tất cả";
      localStorage.setItem("nendatyeu.wizardStep", String(activeStep));
      requestAnimationFrame(updateStickyPreview);
    };

    const navigateToField = (id?: string) => {
      if (!id) return;
      const field = document.getElementById(id);
      if (!field) return;
      const cardId = field.closest<HTMLElement>(".card")?.id;
      const step = STEPS.findIndex((item) => cardId ? item.cards.includes(cardId as never) : false);
      if (step >= 0) { activeStep = step; showAll = false; renderWizard(); }
      field.scrollIntoView({ behavior: "smooth", block: "center" });
      field.focus();
    };

    const renderErrors = () => {
      const state = api.getState();
      const issues: ValidationIssue[] = window.NendatYeuCore.validateProject(state).issues;
      document.querySelectorAll<HTMLInputElement | HTMLSelectElement>("input:invalid, select:invalid").forEach((input) => {
        const conditionalPanel = input.closest<HTMLElement>("#pvd-tcvn-only, #pvd-vacuum-settings, #pvd-settings, #cdm-settings, #sand-drain-settings, #sand-column-settings, #bamboo-pile-settings, #excavation-replacement-settings");
        if (input.disabled || conditionalPanel?.style.display === "none") return;
        if (!input.id) {
          const row = input.closest("tr");
          const rowIndex = row ? Array.from(row.parentElement?.children ?? []).indexOf(row) : 0;
          const inputIndex = row ? Array.from(row.querySelectorAll("input, select")).indexOf(input) : 0;
          input.id = "soil-control-" + rowIndex + "-" + inputIndex;
        }
        const label = input.labels?.[0]?.textContent?.trim() || input.title || input.id;
        issues.push({ path: input.id, message: label + ": giá trị ngoài miền cho phép.", severity: "error" });
      });
      if (issues.length === 0) {
        errors.className = "engineering-errors valid";
        errors.innerHTML = `<strong>Dữ liệu hợp lệ.</strong> Có thể chuyển sang bước Kết quả.`;
        return;
      }
      errors.className = "engineering-errors invalid";
      errors.innerHTML = `<strong>${issues.length} lỗi cần xử lý</strong><div>${issues.slice(0, 8).map((issue) => {
        const id = issue.path.includes(".") ? fieldIdForPath(issue.path) : issue.path;
        return `<button type="button" data-field="${id ?? ""}">${issue.message}</button>`;
      }).join("")}</div>`;
    };

    const updateUndoButtons = () => {
      (document.getElementById("undo-project") as HTMLButtonElement).disabled = undoStack.length === 0;
      (document.getElementById("redo-project") as HTMLButtonElement).disabled = redoStack.length === 0;
    };
    const setStatus = (text: string, state: "dirty" | "saved" | "neutral" = "neutral") => {
      const status = document.getElementById("save-status")!;
      status.textContent = text;
      status.dataset.state = state;
    };
    const saveHistory = (snapshot: Record<string, any>) => {
      const metadata = createReportMetadata(snapshot);
      const history = JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]") as any[];
      if (history[0]?.checksum === metadata.checksum) return;
      history.unshift({ timestamp: metadata.generatedAt, checksum: metadata.checksum, state: snapshot });
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, 10)));
    };
    const autoSave = () => {
      const snapshot = api.getState();
      localStorage.setItem(AUTOSAVE_KEY, JSON.stringify({ timestamp: new Date().toISOString(), state: snapshot }));
      saveHistory(snapshot);
      setStatus("Đã tự lưu cục bộ", dirty ? "dirty" : "saved");
    };
    const markDirty = () => {
      dirty = true;
      setStatus("Có thay đổi chưa xuất file", "dirty");
      if (autosaveTimer) clearTimeout(autosaveTimer);
      autosaveTimer = setTimeout(autoSave, 800);
    };

    const restoreSnapshot = (snapshot: Record<string, any>, addRedo = true) => {
      if (addRedo) redoStack.push(api.getState());
      api.replaceState(snapshot);
      markDirty();
      renderErrors();
      updateUndoButtons();
    };

    const showDrawer = (title: string, content: string) => {
      drawer.hidden = false;
      drawer.innerHTML = `<div class="drawer-header"><h2>${title}</h2><button type="button" id="close-drawer" aria-label="Đóng">×</button></div><div class="drawer-content">${content}</div>`;
      document.getElementById("close-drawer")!.addEventListener("click", () => { drawer.hidden = true; });
    };

    const renderHistory = () => {
      const history = JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]") as any[];
      showDrawer("Lịch sử dự án", history.length ? `<div class="history-list">${history.map((item, index) => `
        <button type="button" data-history="${index}"><strong>${new Date(item.timestamp).toLocaleString("vi-VN")}</strong><span>${item.checksum}</span></button>`).join("")}</div>` : "<p>Chưa có bản tự lưu.</p>");
      drawer.querySelectorAll<HTMLButtonElement>("[data-history]").forEach((button) => button.addEventListener("click", () => {
        undoStack.push(api.getState());
        restoreSnapshot(history[Number(button.dataset.history)].state, false);
        drawer.hidden = true;
      }));
    };

    const comparisonName = (state: Record<string, any>) => state.treatment?.method ?? "unknown";
    const renderComparison = () => {
      const comparisons = JSON.parse(localStorage.getItem(COMPARISON_KEY) ?? "[]") as any[];
      const rows = comparisons.map((item, index) => `<tr><td>${item.name}</td><td>${item.total ?? "—"}</td><td>${item.remaining ?? "—"}</td><td>${item.u ?? "—"}</td><td><button type="button" data-remove-comparison="${index}">Xóa</button></td></tr>`).join("");
      showDrawer("So sánh phương án", `<button type="button" class="btn btn-primary btn-sm" id="capture-comparison">Thêm phương án hiện tại</button>
        <table class="comparison-table"><thead><tr><th>Phương án</th><th>Tổng lún (cm)</th><th>Lún dư (cm)</th><th>U bàn giao (%)</th><th></th></tr></thead><tbody>${rows || '<tr><td colspan="5">Chưa lưu phương án so sánh.</td></tr>'}</tbody></table>`);
      document.getElementById("capture-comparison")!.addEventListener("click", () => {
        const result = api.getResult(); const state = api.getState();
        if (!result) return;
        comparisons.push({ name: comparisonName(state), total: result.total.toFixed(2), remaining: result.remaining.toFixed(2), u: result.uHandover.toFixed(1), checksum: projectChecksum(state) });
        localStorage.setItem(COMPARISON_KEY, JSON.stringify(comparisons.slice(-8)));
        renderComparison();
      });
      drawer.querySelectorAll<HTMLButtonElement>("[data-remove-comparison]").forEach((button) => button.addEventListener("click", () => {
        comparisons.splice(Number(button.dataset.removeComparison), 1);
        localStorage.setItem(COMPARISON_KEY, JSON.stringify(comparisons)); renderComparison();
      }));
    };

    const renderSensitivity = () => {
      showDrawer("Phân tích độ nhạy", `<div class="sensitivity-controls"><label>Thông số
        <select id="sensitivity-parameter"><option value="Cc">Cc</option><option value="Cv">Cv</option><option value="c">cu/c′</option><option value="chRatio">Ch/Cv</option></select></label>
        <button type="button" class="btn btn-primary btn-sm" id="run-sensitivity">Chạy ±20%</button></div><div id="sensitivity-output"><p>Chọn thông số và chạy phân tích.</p></div>`);
      document.getElementById("run-sensitivity")!.addEventListener("click", () => {
        const parameter = (document.getElementById("sensitivity-parameter") as HTMLSelectElement).value;
        const rows = api.evaluateSensitivity(parameter, [0.8, 1, 1.2]);
        document.getElementById("sensitivity-output")!.innerHTML = `<table class="comparison-table"><thead><tr><th>Biến thiên</th><th>Tổng lún</th><th>Lún dư</th><th>FS</th></tr></thead><tbody>${rows.map((row) => `<tr><td>${((row.factor - 1) * 100).toFixed(0)}%</td><td>${Number(row.result.total).toFixed(2)} cm</td><td>${Number(row.result.remaining).toFixed(2)} cm</td><td>${Number.isFinite(row.result.fs) ? Number(row.result.fs).toFixed(3) : "—"}</td></tr>`).join("")}</tbody></table>`;
      });
    };

    toolbar.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;
      const stepButton = target.closest<HTMLButtonElement>("[data-step]");
      if (stepButton) { activeStep = Number(stepButton.dataset.step); showAll = false; renderWizard(); return; }
      if (target.closest("#wizard-prev")) { activeStep -= 1; renderWizard(); }
      if (target.closest("#wizard-next")) { activeStep += 1; renderWizard(); }
      if (target.closest("#wizard-all")) { showAll = !showAll; renderWizard(); }
      if (target.closest("#undo-project") && undoStack.length) restoreSnapshot(undoStack.pop()!);
      if (target.closest("#redo-project") && redoStack.length) { undoStack.push(api.getState()); restoreSnapshot(redoStack.pop()!, false); }
      if (target.closest("#open-history")) renderHistory();
      if (target.closest("#open-comparison")) renderComparison();
      if (target.closest("#open-sensitivity")) renderSensitivity();
    });
    errors.addEventListener("click", (event) => navigateToField((event.target as HTMLElement).closest<HTMLButtonElement>("[data-field]")?.dataset.field));

    document.addEventListener("focusin", (event) => {
      if ((event.target as HTMLElement).matches("input, select, textarea")) focusSnapshot = api.getState();
    }, true);
    document.addEventListener("change", (event) => {
      if (!(event.target as HTMLElement).matches("input, select, textarea")) return;
      if (focusSnapshot && projectChecksum(focusSnapshot) !== projectChecksum(api.getState())) {
        undoStack.push(focusSnapshot); redoStack = []; updateUndoButtons();
      }
      focusSnapshot = null; markDirty(); setTimeout(renderErrors, 0);
    }, true);
    document.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;
      if (target.closest("#btn-add-layer, #soil-table-body button")) {
        undoStack.push(api.getState()); redoStack = []; updateUndoButtons(); setTimeout(() => { markDirty(); renderErrors(); }, 0);
      }
    }, true);
    window.addEventListener("beforeunload", (event) => { if (dirty) { event.preventDefault(); event.returnValue = ""; } });
    window.addEventListener("nendatyeu:saved", () => { dirty = false; setStatus("Đã xuất file", "saved"); });
    window.addEventListener("nendatyeu:state-replaced", () => { renderErrors(); });

    document.querySelectorAll<HTMLInputElement>('input[type="number"]').forEach((input) => {
      const group = input.closest<HTMLElement>(".form-group");
      if (!group || group.querySelector(".engineering-field-meta")) return;
      const bounds = [input.min ? `min ${input.min}` : "", input.max ? `max ${input.max}` : ""].filter(Boolean).join(" · ") || "theo miền vật lý";
      const meta = element("small", "engineering-field-meta");
      meta.textContent = `Miền: ${bounds} • Nguồn: ${sourceForInput(input)}`;
      group.appendChild(meta);
      input.title = `${input.title ? `${input.title}. ` : ""}${meta.textContent}`;
    });

    const autosave = localStorage.getItem(AUTOSAVE_KEY);
    if (autosave) setStatus("Có bản tự lưu cục bộ", "saved");
    renderWizard(); renderErrors(); updateUndoButtons();
    document.documentElement.dataset.appVersion = APP_VERSION;
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot, { once: true });
  else boot();
}
